'use client';
import TabComponent from '@/componentv2/tab/tab';
import { GetAllCategoriesResult } from '@/sanity.types';
import './getInspired.scss';
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Button from '@/componentv2/button/button';
import FontAwesomeIcon from '@/componentv2/fontAwesomeIcon/fontAwesomeIcon';
import { useRouter } from 'next/navigation';

interface GetInspiredProps {
  tabs: GetAllCategoriesResult;
  onAllClick?: () => void;
}

const GetInspired = ({ tabs, onAllClick }: GetInspiredProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const router = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Define the clear search function
  const clearSearch = () => {
    // Dispatch a custom event to clear search
    const event = new CustomEvent('clearBlogSearch');
    window.dispatchEvent(event);
  };

  const toggleVisibility = () => {
    setIsVisible((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  return (
    <div className="getInspiredWrap">
      <h2>Get inspired</h2>
      <div className="badge desktop-only">
        <span>Popular topics</span>
      </div>
      <div className="mobile-dropdown">
        <div className="blog-category-dropdown" ref={dropdownRef}>
          <Button
            onClick={toggleVisibility}
            aria-haspopup="true"
            aria-expanded={isVisible}
            aria-controls="blog-dropdown"
            className="blog-type-btn"
          >
            Popular topics
            <FontAwesomeIcon
              name="fa-chevron-down"
              style="fa-solid"
              fontSize="14px"
            />
          </Button>
          <div
            className={`blogCateDropdown ${isVisible ? 'show' : 'hide'}`}
            id="blog-dropdown"
            role="menu"
            aria-labelledby="blog-dropdown"
          >
            {tabs?.map((category) => {
              const href =
                category.title === 'All'
                  ? '/blog'
                  : `/blog/category/${category?.slug?.current}`;
              return (
                <Link
                  key={category?._id || category.title}
                  role="menuitem"
                  href={href}
                  onClick={(e) => {
                    if (category.title === 'All') {
                      e.preventDefault(); // Prevent navigation and scroll
                      clearSearch(); // Clear the search
                      onAllClick?.();
                      return;
                    }
                    if (category.title !== 'All') {
                      e.preventDefault();
                      router.push(href);
                    }
                  }}
                >
                  {category?.title}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      <div className="desktop-tabs">
        <TabComponent tabs={tabs} mainURL="/blog" onAllClick={clearSearch} />
      </div>
    </div>
  );
};

export default GetInspired;
